(function() {
  'use strict';

  function getConfig() {
    const scriptTag = document.querySelector('script[data-inventory-config]');
    if (scriptTag) {
      try {
        return JSON.parse(scriptTag.getAttribute('data-inventory-config'));
      } catch (e) {
        console.error('Failed to parse inventory config:', e);
      }
    }
    
    if (window.inventoryApiToken && window.productHandle) {
      return {
        storefrontAccessToken: window.inventoryApiToken,
        handle: window.productHandle
      };
    }
    
    return null;
  }

  // Check inventory for collection and search page products
  async function checkCollectionProductsInventory() {

    const productItems = document.querySelectorAll('.product-item');
    if (productItems.length === 0) return;

    const STOREFRONT_ACCESS_TOKEN = window.inventoryApiToken;
    const url = `/api/2024-07/graphql.json`;

    for (const productItem of productItems) {
      const productLink = productItem.querySelector('a[href*="/products/"]');
      if (!productLink) continue;

      const productUrl = productLink.getAttribute('href');
      const handle = productUrl.split('/products/')[1]?.split('?')[0];
      if (!handle) continue;

      const addToCartButton = productItem.querySelector('button[data-action="add-to-cart"]');
      if (!addToCartButton) continue;

      const buttonText = addToCartButton.innerText.trim().toLowerCase();
      if (buttonText.includes('sold out') || buttonText.includes('out of stock')) {
        continue;
      }

      const query = `
        query MyQuery($handle: String!) {
          product(handle: $handle) {
            id
            title
            availableForSale
            variants(first: 250) {
              nodes {
                quantityAvailable
              }
            }
            metafield(namespace: "custom", key: "acu_product_allow_back_order") {
              value
            }
          }
        }
      `;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
          },
          body: JSON.stringify({ 
            query,
            variables: { handle: handle }
          })      
        });
        
        const result = await response.json();

        if (result.errors) {
          console.error('GraphQL errors for', handle, ':', result.errors);
          continue;
        }

        if (result.data && result.data.product) {
          const productData = result.data.product;
          const totalInventory = productData.variants.nodes.reduce((sum, variant) => {
            return sum + (variant.quantityAvailable || 0);
          }, 0);

          const hasZeroStockAndContinuesSelling = productData.variants.nodes && productData.variants.nodes.some(variant => {
            return variant.quantityAvailable <= 0 && productData.availableForSale;
          });
          const metafieldEnabled = productData.metafield && productData.metafield.value === "true";
          const isBackOrder = hasZeroStockAndContinuesSelling && metafieldEnabled;

          if (totalInventory <= 0 && !isBackOrder) {
            const soldOutLabel = (typeof window.soldOutText !== 'undefined') ? window.soldOutText : 'Sold Out';
            addToCartButton.innerText = soldOutLabel;
            addToCartButton.classList.add('button--disabled');
            addToCartButton.classList.remove('button--primary');
            addToCartButton.disabled = true;
            addToCartButton.removeAttribute('data-action');
            addToCartButton.type = 'button';

            const inventoryDisplay = productItem.querySelector('.product-item__inventory');
            if (inventoryDisplay) {
              inventoryDisplay.innerText = soldOutLabel;
              inventoryDisplay.classList.remove('inventory--high');
              inventoryDisplay.classList.add('inventory--low');
            }
          }
        }
      } catch (error) {
        console.error('Error checking inventory for', handle, ':', error);
      }
    }
  }

  async function checkProductInventory() {
    const config = getConfig();
    if (!config || !config.storefrontAccessToken || !config.handle) {
      return;
    }
    
    const STOREFRONT_ACCESS_TOKEN = config.storefrontAccessToken;
    const url = `/api/2024-07/graphql.json`;
    const handle = config.handle;
    
    const query = `
      query MyQuery($handle: String!) {
        product(handle: $handle) {
          id
          title
          availableForSale
          variants(first: 250) {
            nodes {
              quantityAvailable
            }
          }
          metafield(namespace: "custom", key: "acu_product_allow_back_order") {
            value
          }
        }
      }
    `;
    
    function waitForButton(maxAttempts = 50, interval = 100) {
      return new Promise((resolve) => {
        let attempts = 0;
        const checkButton = () => {
          const button = document.querySelector('.product-form__add-button');
          if (button || attempts >= maxAttempts) {
            resolve(button);
          } else {
            attempts++;
            setTimeout(checkButton, interval);
          }
        };
        checkButton();
      });
    }
    
    try {
      await waitForButton();
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({ 
          query,
          variables: { handle: handle }
        })      
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        return;
      } else if (result.data && result.data.product) {
        const productData = result.data.product;
        const totalInventory = productData.variants.nodes.reduce((sum, variant) => {
          return sum + (variant.quantityAvailable || 0);
        }, 0);
        
        window.productTotalInventoryFromGraphQL = totalInventory;

        const hasZeroStockAndContinuesSelling = productData.variants.nodes && productData.variants.nodes.some(variant => {
          return variant.quantityAvailable <= 0 && productData.availableForSale;
        });
        const metafieldEnabled = productData.metafield && productData.metafield.value === "true";
        const isBackOrder = hasZeroStockAndContinuesSelling && metafieldEnabled;
        
        function updateButtonState() {
          const button = document.querySelector('.product-form__add-button');
          if (!button) return;
          
          // Don't overwrite if button is already set to "Back Order" by another script
          const buttonText = button.innerText.trim().toLowerCase();
          if (buttonText === 'back order' || buttonText === 'pre order') {
            return;
          }
          
          if (totalInventory <= 0 && !isBackOrder) {
            // Update to Sold Out
            const soldOutLabel = (typeof window.soldOutText !== 'undefined') ? window.soldOutText : 'Sold Out';
            button.innerText = soldOutLabel;
            button.classList.add('button--disabled');
            button.disabled = true;
            button.removeAttribute('data-action');
            button.type = 'button';
            
            const stockAvailability = document.getElementById('product-form-stock');
            if (stockAvailability) {
              const currentText = stockAvailability.textContent.trim();
              const backOrderMessage = document.getElementById('back_order_availability_message');
              const isBackOrderMessage = backOrderMessage && currentText === backOrderMessage.value;
              
              if (!isBackOrderMessage) {
                const soldOutText = (typeof window.soldOutText !== 'undefined') ? window.soldOutText : 'Sold Out';
                stockAvailability.innerText = soldOutText;
                stockAvailability.classList.remove('inventory--high');
                stockAvailability.classList.remove('inventory--low');
                stockAvailability.classList.add('stock-status');
              }
            }
            
            // Hide quantity selector
            const quantityWrapper = document.querySelector('.product-form__quantity-wrapper');
            if (quantityWrapper) {
              quantityWrapper.style.display = 'none';
            }
            
            // Hide Get Better Price button/section
            const betterPriceSection = document.querySelector('.better-price-section');
            if (betterPriceSection) {
              betterPriceSection.style.display = 'none';
            }
            const betterPriceLink = document.querySelector('.betterPriceLink:not(.better-price-section .betterPriceLink)');
            if (betterPriceLink) {
              betterPriceLink.style.display = 'none';
            }
            // Also check for app block version
            const betterPriceAppBlock = document.querySelector('[data-shopify-app-block="mega-better-price"]');
            if (betterPriceAppBlock) {
              betterPriceAppBlock.style.display = 'none';
            }
            
            // Hide shipping indicators
            const freeShippingItem = document.querySelector('.free-shipping-store-item');
            if (freeShippingItem) {
              freeShippingItem.style.display = 'none';
            }
            const shippingEstimator = document.querySelector('.shipping-estimator');
            if (shippingEstimator) {
              shippingEstimator.closest('.product-block-list__item--shipping')?.style.setProperty('display', 'none', 'important');
            }
            
            // Hide all store indicators (pickup details, store availability)
            const pickupDetailsDiv = document.querySelector('.store-pickup-details');
            if (pickupDetailsDiv) {
              pickupDetailsDiv.style.display = 'none';
            }
            
            const pickupDetailsLink = document.querySelector('.store-availability-container');
            if (pickupDetailsLink) {
              pickupDetailsLink.style.display = 'none';
            }
            
            const storeAvailabilityInfo = document.querySelector('.store-availability-information');
            if (storeAvailabilityInfo) {
              storeAvailabilityInfo.style.display = 'none';
            }
            
            const storeAvailabilityModal = document.querySelector('.store-availabilities-modal');
            if (storeAvailabilityModal) {
              storeAvailabilityModal.style.display = 'none';
            }
            
            const storeAvailabilityContainer = document.querySelector('.product-meta__store-availability-container');
            if (storeAvailabilityContainer) {
              storeAvailabilityContainer.style.display = 'none';
            }
            
            // Hide Make an Offer block (mega-better-price app block)
            const makeOfferBlock = document.querySelector('[data-shopify-app-block="mega-better-price"]');
            if (makeOfferBlock) {
              makeOfferBlock.style.display = 'none';
            }
            
            // Hide the parent block item if it contains the make offer block
            const makeOfferBlockItem = document.querySelector('.product-info__block-item:has([data-shopify-app-block="mega-better-price"])');
            if (makeOfferBlockItem) {
              makeOfferBlockItem.style.display = 'none';
            }
            
            // Also hide any custom-pdp-selector that might contain make offer
            const customPdpSelector = document.querySelector('.custom-pdp-selector');
            if (customPdpSelector) {
              const makeOfferLink = customPdpSelector.querySelector('a[href*="make"], a[href*="offer"], .betterPriceLink');
              if (makeOfferLink) {
                customPdpSelector.style.display = 'none';
              }
            }
            
            // Hide warranty block
            const warrantyBlock = document.querySelector('.product-upsell-block');
            if (warrantyBlock) {
              warrantyBlock.style.display = 'none';
            }
            // Also hide warranty checkbox and related elements
            const warrantyCheckbox = document.getElementById('add-warranty');
            if (warrantyCheckbox) {
              warrantyCheckbox.closest('.product-upsell-block')?.style.setProperty('display', 'none', 'important');
            }
            // Hide the parent block item if it contains warranty
            const warrantyBlockItem = document.querySelector('.product-info__block-item:has(.product-upsell-block)');
            if (warrantyBlockItem && warrantyBlockItem.querySelector('#add-warranty')) {
              warrantyBlockItem.style.display = 'none';
            }
          } else if (totalInventory > 0 && button.disabled && button.classList.contains('button--disabled')) {
            // Restore Add to Cart if inventory is available
            const addToCartLabel = (typeof window.addToCartText !== 'undefined') ? window.addToCartText : 'Add to cart';
            button.innerText = addToCartLabel;
            button.classList.remove('button--disabled');
            button.disabled = false;
            button.setAttribute('data-action', 'add-to-cart');
            button.type = 'submit';
            
            const stockAvailability = document.getElementById('product-form-stock');
            if (stockAvailability) {
              stockAvailability.classList.remove('stock-status');
              stockAvailability.classList.remove('inventory--low');
              stockAvailability.classList.add('inventory--high');
            }
            
            // Show quantity selector if it was hidden
            const quantityWrapper = document.querySelector('.product-form__quantity-wrapper');
            if (quantityWrapper) {
              quantityWrapper.style.display = '';
            }
            
            // Show Get Better Price button/section if it was hidden
            const betterPriceSection = document.querySelector('.better-price-section');
            if (betterPriceSection) {
              betterPriceSection.style.display = '';
            }
            const betterPriceLink = document.querySelector('.betterPriceLink:not(.better-price-section .betterPriceLink)');
            if (betterPriceLink) {
              betterPriceLink.style.display = '';
            }
            const betterPriceAppBlock = document.querySelector('[data-shopify-app-block="mega-better-price"]');
            if (betterPriceAppBlock) {
              betterPriceAppBlock.style.display = '';
            }
            
            // Show shipping indicators if they were hidden
            const freeShippingItem = document.querySelector('.free-shipping-store-item');
            if (freeShippingItem) {
              freeShippingItem.style.display = '';
            }
            const shippingEstimator = document.querySelector('.shipping-estimator');
            if (shippingEstimator) {
              shippingEstimator.closest('.product-block-list__item--shipping')?.style.removeProperty('display');
            }
            
            // Show store indicators if they were hidden
            const pickupDetailsDiv = document.querySelector('.store-pickup-details');
            if (pickupDetailsDiv) {
              pickupDetailsDiv.style.display = '';
            }
            
            const pickupDetailsLink = document.querySelector('.store-availability-container');
            if (pickupDetailsLink) {
              pickupDetailsLink.style.display = '';
            }
            
            const storeAvailabilityInfo = document.querySelector('.store-availability-information');
            if (storeAvailabilityInfo) {
              storeAvailabilityInfo.style.display = '';
            }
            
            const storeAvailabilityModal = document.querySelector('.store-availabilities-modal');
            if (storeAvailabilityModal) {
              storeAvailabilityModal.style.display = '';
            }
            
            const storeAvailabilityContainer = document.querySelector('.product-meta__store-availability-container');
            if (storeAvailabilityContainer) {
              storeAvailabilityContainer.style.display = '';
            }
            
            // Show Make an Offer block if it was hidden
            const makeOfferBlock = document.querySelector('[data-shopify-app-block="mega-better-price"]');
            if (makeOfferBlock) {
              makeOfferBlock.style.display = '';
            }
            
            const makeOfferBlockItem = document.querySelector('.product-info__block-item:has([data-shopify-app-block="mega-better-price"])');
            if (makeOfferBlockItem) {
              makeOfferBlockItem.style.display = '';
            }
            
            const customPdpSelector = document.querySelector('.custom-pdp-selector');
            if (customPdpSelector) {
              customPdpSelector.style.display = '';
            }
            
            // Show warranty block if it was hidden
            const warrantyBlock = document.querySelector('.product-upsell-block');
            if (warrantyBlock) {
              warrantyBlock.style.display = '';
            }
            const warrantyCheckbox = document.getElementById('add-warranty');
            if (warrantyCheckbox) {
              warrantyCheckbox.closest('.product-upsell-block')?.style.removeProperty('display');
            }
            const warrantyBlockItem = document.querySelector('.product-info__block-item:has(.product-upsell-block)');
            if (warrantyBlockItem && warrantyBlockItem.querySelector('#add-warranty')) {
              warrantyBlockItem.style.display = '';
            }
          }
        }
        
        // Update button state immediately and after a short delay to handle race conditions
        updateButtonState();
        setTimeout(updateButtonState, 200);
        setTimeout(updateButtonState, 500);
      } else if (result.data && result.data.product === null) {
        console.warn('Product not found for handle:', handle);
      } else {
        console.warn('Unexpected response format:', result);
      }
    } catch (error) {
      console.error('Error checking product inventory:', error);
    }
  }

  // Check if we're on a product page or collection/search page
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // Check for product detail page
      if (document.querySelector('.product-form__add-button')) {
        checkProductInventory();
      }
      // Check for collection or search page
      if (document.querySelector('.product-list--collection') || document.querySelector('[data-section-type="collection"]') || document.querySelector('[data-section-type="search"]')) {
        checkCollectionProductsInventory();
      }
    });
  } else {
    // Check for product detail page
    if (document.querySelector('.product-form__add-button')) {
      checkProductInventory();
    }
    // Check for collection or search page
    if (document.querySelector('.product-list--collection') || document.querySelector('[data-section-type="collection"]') || document.querySelector('[data-section-type="search"]')) {
      checkCollectionProductsInventory();
    }
  }
})();
